import { useState, useEffect, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import InvestorDashboard from './InvestorDashboard';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { AppLayout } from '../components/layout/AppLayout';
import { useAuth } from '../context/AuthContext';
import { getDashboardType, canLogVisit, canLogSale } from '../utils/roles';
import api from '../utils/api';
import toast from 'react-hot-toast';
import DemoInbox from '../components/DemoInbox';

const PRIORITY_DOT = { urgent:'#EF4444', high:'#F59E0B', medium:'#403EC9', low:'#22C55E' };

function TasksWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get('/tasks/stats').then(r => setStats(r.data.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const markDone = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await api.patch(`/tasks/${id}`, { status: 'done' });
      toast.success('Done ✓');
      load();
    } catch { toast.error('Failed'); }
  };

  const fmtDue = d => {
    if (!d) return null;
    const diff = Math.round((new Date(d) - new Date()) / 86400000);
    if (diff < 0)  return { label: `${Math.abs(diff)}d overdue`, color:'#EF4444' };
    if (diff === 0) return { label: 'Due today', color:'#D97706' };
    if (diff === 1) return { label: 'Tomorrow', color:'#D97706' };
    return { label: `${diff}d left`, color:'var(--text-muted)' };
  };

  if (loading) return null;

  const open = Number(stats?.my_open || 0);
  const overdue = Number(stats?.my_overdue || 0);
  const upcoming = stats?.upcoming || [];

  return (
    <div className="card" style={{ marginBottom:14 }}>
      <div className="card-header" style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <h3 style={{ fontSize:'0.9rem', display:'flex', alignItems:'center', gap:8 }}>
          ✅ My Tasks
          {overdue > 0 && (
            <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'2px 8px', borderRadius:99, background:'#FEE2E2', color:'#991B1B' }}>
              {overdue} overdue
            </span>
          )}
        </h3>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>{open} open</span>
          <Link to="/tasks" style={{ fontSize:'0.8rem', color:'#403EC9', fontWeight:600, textDecoration:'none' }}>View all →</Link>
        </div>
      </div>
      <div className="card-body" style={{ padding: upcoming.length ? '8px 16px' : '12px 16px' }}>
        {upcoming.length === 0 ? (
          <div style={{ color:'var(--text-muted)', fontSize:'0.83rem', textAlign:'center', padding:'8px 0' }}>
            {open === 0 ? '🎉 All caught up!' : 'No upcoming tasks due soon'}
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {upcoming.map(t => {
              const due = fmtDue(t.due_date);
              return (
                <div key={t.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 0',
                  borderBottom:'1px solid #F3F4F6' }}>
                  <span style={{ width:8, height:8, borderRadius:'50%', background: PRIORITY_DOT[t.priority] || '#403EC9', flexShrink:0 }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'0.85rem', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.title}</div>
                    {due && <div style={{ fontSize:'0.72rem', color:due.color, fontWeight:500 }}>{due.label}</div>}
                  </div>
                  <button onClick={e => markDone(t.id, e)} style={{ fontSize:'0.7rem', padding:'2px 10px', borderRadius:6,
                    border:'1px solid #16A34A', background:'#F0FDF4', color:'#166534', cursor:'pointer', fontWeight:700, flexShrink:0 }}>
                    Done
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <Link to="/tasks/new" style={{ display:'block', textAlign:'center', marginTop:8, fontSize:'0.8rem',
          color:'#403EC9', fontWeight:600, textDecoration:'none' }}>
          + Assign a task
        </Link>
      </div>
    </div>
  );
}

const fmt = (n) => {
  if (!n) return '₹0';
  if (n >= 10000000) return `₹${(n/10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${(n/100000).toFixed(1)}L`;
  if (n >= 1000)     return `₹${(n/1000).toFixed(0)}K`;
  return `₹${n}`;
};
// Full, non-abbreviated currency — used for tax-inclusive "Revenue" KPI figures specifically
// (accounting team asked to see the actual number, not a K/L/Cr shorthand)
const fmtFull = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');
const CLR = ['#403EC9','#6B69E0','#9B99F0','#16A34A','#D97706','#DC2626'];

// Shared revenue chart — used by all leadership dashboards (VP, Admin, Executive/CEO)
// Shows total revenue line always; device + consumable dashed lines when data exists.
// top margin = 20px so r:4 dots are never clipped at domain max.
function RevenueChart({ trend, height = 210 }) {
  const data = trend || [];
  const hasBreakdown = data.some(d => parseFloat(d.device_revenue) > 0 || parseFloat(d.consumable_revenue) > 0);
  const mx = Math.max(...data.flatMap(d => [
    parseFloat(d.actual_revenue)     || 0,
    parseFloat(d.device_revenue)     || 0,
    parseFloat(d.consumable_revenue) || 0,
  ]), 1);
  const yDomain = [0, Math.ceil(mx * 1.25 / 100000) * 100000 || 100000];

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top:20, right:20, bottom:8, left:8 }}>
        <XAxis dataKey="month_label" tick={{ fontSize:10, fill:'#9CA3AF' }} tickLine={false} axisLine={{ stroke:'#E5E7EB' }} interval={0} angle={-35} textAnchor="end" height={40} />
        <YAxis tickFormatter={v => fmt(v)} tick={{ fontSize:11, fill:'#6B7280' }} tickLine={false} axisLine={false} width={56} domain={yDomain} />
        <Tooltip formatter={(v, name) => v != null ? [fmt(v), name] : []} contentStyle={{ fontSize:12 }} />
        <Line type="monotone" dataKey="actual_revenue" stroke="#403EC9" strokeWidth={2.5} dot={{ r:4, fill:'#403EC9', strokeWidth:0 }} activeDot={{ r:6 }} name="Total Revenue" connectNulls={false} />
        {hasBreakdown && <>
          <Line type="monotone" dataKey="device_revenue"     stroke="#16A34A" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="Device"     connectNulls={false} />
          <Line type="monotone" dataKey="consumable_revenue" stroke="#D97706" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="Consumable" connectNulls={false} />
        </>}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const type = getDashboardType(user?.role_slug || user?.role);
  if (type === 'distributor') return <Navigate to="/distributor-portal" replace />;
  if (type === 'dispatch')   return <Navigate to="/dispatch-dashboard" replace />;
  if (type === 'investor')  return <InvestorDashboard />;
  if (type === 'finance')   return <FinanceDashboard />;
  if (type === 'field')     return <FieldDashboard />;
  if (type === 'executive') return <ExecutiveDashboard />;
  if (type === 'vp')        return <VPDashboard />;
  return <AdminDashboard />;
}

// ─── UX FIX 1 + 2 + 3: My Day page for field reps ────────────────
function FieldDashboard() {
  const { user } = useAuth();
  const isBDE  = user?.role_slug === 'bde' || user?.role === 'bde';
  const isRSM  = user?.role === 'rsm' || user?.role_slug === 'rsm';
  const [myDay, setMyDay]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [showVisit, setShowVisit]   = useState(false);
  const [showSale,  setShowSale]    = useState(false);
  const [quickDoc,  setQuickDoc]    = useState(null);
  const [todayFeed, setTodayFeed]   = useState([]);
  const [followups, setFollowups]   = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);

  useEffect(() => {
    api.get('/dashboard/my-day')
      .then(r => setMyDay(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!isBDE) return;
    setFeedLoading(true);
    Promise.all([
      api.get('/activities/feed', { params: { my_day: 'true' } }),
      api.get('/activities/my-followups'),
    ]).then(([feedRes, fuRes]) => {
      setTodayFeed(feedRes.data.data || []);
      setFollowups(fuRes.data.data || []);
    }).catch(() => {}).finally(() => setFeedLoading(false));
  }, [isBDE]);

  const hr = new Date().getHours();
  const greet = hr < 12 ? 'Good morning' : hr < 17 ? 'Good afternoon' : 'Good evening';
  const stats = myDay?.visit_stats;

  return (
    <AppLayout title="My Day">
      <TasksWidget />
      {/* Hero */}
      <div style={{ background:'var(--bg-sidebar)', borderRadius:'var(--radius-xl)', padding:'20px 24px', marginBottom:16, color:'#fff', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', width:180, height:180, borderRadius:'50%', background:'rgba(255,255,255,0.05)', top:-50, right:-30 }} />
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ fontSize:'0.78rem', color:'rgba(255,255,255,0.5)', marginBottom:3 }}>{greet}</div>
          <div style={{ fontSize:'1.3rem', fontWeight:700 }}>{user?.name}{isRSM ? ' — RSM' : ''}</div>
          <div style={{ fontSize:'0.78rem', color:'rgba(255,255,255,0.4)', marginTop:2 }}>
            {user?.territory_name || 'No territory'} · {new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long' })}
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {canLogVisit(user?.role) && (
          <button className="btn btn-primary" onClick={() => setShowVisit(true)} style={{ gap:6 }}>
            <span>📍</span> Log Visit
          </button>
        )}
        {canLogSale(user?.role) && (
          <button className="btn btn-secondary" onClick={() => setShowSale(true)} style={{ gap:6 }}>
            <span>💰</span> Log Sale
          </button>
        )}
        <a href="/doctors"  className="btn btn-ghost">My doctors →</a>
        <a href="/travel"   className="btn btn-ghost">Travel plan →</a>
        {isRSM && <a href="/actuals" className="btn btn-secondary" style={{ color:'var(--warning)' }}>✓ Approve actuals</a>}
      </div>

      {/* UX FIX: KPIs always show targets, never just zeros */}
      <div className="kpi-grid" style={{ marginBottom:16 }}>
        <KpiCard label="Visits MTD"       value={stats?.total_visits || 0}     color="#403EC9" icon="📍" />
        <KpiCard label="Demos MTD"        value={stats?.demos || 0}            color="#16A34A" icon="🎯" />
        <KpiCard label="Overdue follow-ups" value={stats?.overdue || 0}        color={Number(stats?.overdue) > 0 ? '#DC2626' : '#16A34A'} icon="⚠️" />
        <KpiCard label="Unique doctors"   value={stats?.unique_doctors || 0}   color="#2563EB" icon="👨‍⚕️" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        {/* Today's beat plan */}
        <TodayPlan plan={myDay?.today_plan || []} loading={loading} />

        {/* RSM: pending approvals / Rep: follow-ups */}
        {isRSM
          ? <PendingPanel items={myDay?.pending_approvals || []} loading={loading} />
          : <FollowUpsPanel items={myDay?.follow_ups || []} loading={loading} />
        }
      </div>

      {/* UX FIX 3: 90D focus doctors — key list for today */}
      {(myDay?.top_90d_doctors || []).length > 0 && (
        <div className="card" style={{ marginTop:14 }}>
          <div className="card-header">
            <div>
              <h3 style={{ fontSize:'0.95rem' }}>90-day focus doctors — not visited this month</h3>
              <div style={{ fontSize:'0.75rem', color:'var(--text-secondary)', marginTop:2 }}>
                {myDay.top_90d_doctors.length} priority doctors need attention
              </div>
            </div>
            <a href="/doctors?focus_90d=true" className="btn btn-ghost btn-sm">View all →</a>
          </div>
          <div className="card-body no-pad">
            <div className="table-wrap">
              <table>
                <thead><tr><th>Doctor</th><th>District</th><th>Priority</th><th>Focus Products</th><th>LTV</th><th>90D Goal</th><th></th></tr></thead>
                <tbody>
                  {myDay.top_90d_doctors.map(d => (
                    <tr key={d.id}>
                      <td style={{ fontWeight:600 }}>{d.name}</td>
                      <td style={{ fontSize:'0.78rem', color:'var(--text-secondary)' }}>{d.district}</td>
                      <td><span className={`badge badge-${(d.priority||'b').toLowerCase()}`}>{d.priority}</span></td>
                      <td style={{ fontSize:'0.78rem' }}>
                        {[d.focus_product_1, d.focus_product_2].filter(Boolean).join(' + ')}
                      </td>
                      <td style={{ fontWeight:600, fontSize:'0.82rem' }}>{d.ltv_12m ? fmt(d.ltv_12m) : '—'}</td>
                      <td style={{ fontSize:'0.75rem', color:'var(--text-secondary)', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {d.outcome_goal_90d || '—'}
                      </td>
                      <td>
                        <button className="btn btn-primary btn-sm" style={{ fontSize:'0.72rem', padding:'3px 8px' }}
                          onClick={() => { setQuickDoc(d); setShowVisit(true); }}>
                          Log Visit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* BDE My Day — today's activity + upcoming follow-ups */}
      {isBDE && (
        <>
          <BdeMyDayPanel activities={todayFeed} loading={feedLoading} />
          <BdeFollowupsPanel followups={followups} loading={feedLoading} />
        </>
      )}

      <RecommendationsPanel onLogVisit={(doc) => { setQuickDoc(doc); setShowVisit(true); }} />

      {/* Demos assigned to this rep/RSM/VP */}
      <DemoInbox />
      {/* Conversion tasks assigned to this rep/RSM */}
      <ConversionTasksPanel />

      {showVisit && <QuickVisitModal onClose={() => { setShowVisit(false); setQuickDoc(null); }} prefilledDoctor={quickDoc} />}
      {showSale  && <QuickSaleModal  onClose={() => setShowSale(false)} />}
    </AppLayout>
  );
}

// ─── BDE: Today's Activity Panel ─────────────────────────────────────────────
const ACT_ICON = { call:'📞', whatsapp:'💬', email:'📧', visit:'📍', demo:'🎯', note:'📝', meeting:'🤝' };
const ACT_SOURCE_BADGE = {
  visit:            { label:'Field Visit',    bg:'#DBEAFE', color:'#1E40AF' },
  account_activity: { label:'Account',        bg:'#F3F4F6', color:'#374151' },
  bde_call:         { label:'Prospect Call',  bg:'#FEF3C7', color:'#92400E' },
};
const OUTCOME_COLOR = {
  interested:'#16A34A', not_interested:'#DC2626', callback:'#D97706',
  no_answer:'#6B7280', busy:'#6B7280', answered_qualified:'#16A34A',
  converted:'#16A34A', follow_up:'#2563EB', demo_scheduled:'#7C3AED',
  order_placed:'#16A34A',
};

function BdeMyDayPanel({ activities, loading }) {
  const today = new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long' });
  return (
    <div className="card" style={{ marginTop:14 }}>
      <div className="card-header">
        <div>
          <h3 style={{ fontSize:'0.95rem' }}>My Activity Today</h3>
          <div style={{ fontSize:'0.75rem', color:'var(--text-secondary)', marginTop:2 }}>{today}</div>
        </div>
        <a href="/visits" className="btn btn-ghost btn-sm">All activity →</a>
      </div>
      <div className="card-body" style={{ padding: activities.length ? 0 : undefined }}>
        {loading ? (
          <div style={{ color:'var(--text-muted)', fontSize:'0.82rem' }}>Loading…</div>
        ) : activities.length === 0 ? (
          <div style={{ color:'var(--text-muted)', fontSize:'0.82rem', textAlign:'center', padding:'20px 0' }}>
            No activity logged today yet. Start by logging a call or note on an account.
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column' }}>
            {activities.map((a, i) => {
              const badge = ACT_SOURCE_BADGE[a.source] || ACT_SOURCE_BADGE.account_activity;
              return (
                <div key={a.id} style={{
                  display:'flex', gap:12, padding:'12px 20px',
                  borderBottom: i < activities.length - 1 ? '1px solid var(--border)' : 'none',
                  alignItems:'flex-start',
                }}>
                  <div style={{ fontSize:'1.3rem', flexShrink:0, marginTop:1 }}>
                    {ACT_ICON[a.activity_type] || '📋'}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:3 }}>
                      <span style={{ fontWeight:700, fontSize:'0.85rem' }}>{a.entity_name || 'Unknown'}</span>
                      <span style={{ fontSize:'0.72rem', padding:'1px 7px', borderRadius:99, background: badge.bg, color: badge.color, fontWeight:600 }}>
                        {badge.label}
                      </span>
                      {a.entity_subtype && a.entity_subtype !== 'Doctor' && (
                        <span style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{a.entity_subtype}</span>
                      )}
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom: a.notes ? 4 : 0 }}>
                      <span style={{ fontSize:'0.78rem', fontWeight:600, textTransform:'capitalize', color:'var(--text-secondary)' }}>
                        {a.activity_type}
                      </span>
                      {a.outcome && (
                        <span style={{ fontSize:'0.72rem', padding:'1px 7px', borderRadius:99, background: (OUTCOME_COLOR[a.outcome] || '#6B7280') + '18', color: OUTCOME_COLOR[a.outcome] || '#6B7280', fontWeight:600 }}>
                          {a.outcome.replace(/_/g,' ')}
                        </span>
                      )}
                      {a.duration_mins && (
                        <span style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{a.duration_mins} min</span>
                      )}
                    </div>
                    {a.notes && (
                      <div style={{ fontSize:'0.78rem', color:'var(--text-secondary)', marginBottom:4, fontStyle:'italic' }}>
                        "{a.notes}"
                      </div>
                    )}
                    {a.follow_up_date && (
                      <div style={{ fontSize:'0.72rem', fontWeight:600, color:'#2563EB' }}>
                        📅 Follow-up: {new Date(a.follow_up_date).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', flexShrink:0, marginTop:2 }}>
                    {new Date(a.created_at).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── BDE: Upcoming Follow-ups Panel ──────────────────────────────────────────
function BdeFollowupsPanel({ followups, loading }) {
  if (!loading && followups.length === 0) return null;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="card" style={{ marginTop:14 }}>
      <div className="card-header">
        <div>
          <h3 style={{ fontSize:'0.95rem' }}>Upcoming Follow-ups</h3>
          <div style={{ fontSize:'0.75rem', color:'var(--text-secondary)', marginTop:2 }}>
            Commitments you've made across accounts, doctors and prospects
          </div>
        </div>
        <span style={{ fontSize:'0.82rem', fontWeight:700, color:'var(--text-secondary)' }}>
          {followups.length} pending
        </span>
      </div>
      <div className="card-body" style={{ padding: followups.length ? 0 : undefined }}>
        {loading ? (
          <div style={{ color:'var(--text-muted)', fontSize:'0.82rem' }}>Loading…</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column' }}>
            {followups.map((f, i) => {
              const isOverdue = f.follow_up_date < today;
              const isToday   = f.follow_up_date === today;
              return (
                <div key={f.id} style={{
                  display:'flex', gap:12, padding:'11px 20px', alignItems:'center',
                  borderBottom: i < followups.length - 1 ? '1px solid var(--border)' : 'none',
                  background: isOverdue ? '#FFF1F2' : isToday ? '#FEFCE8' : 'transparent',
                }}>
                  <div style={{ fontSize:'1.1rem', flexShrink:0 }}>
                    {isOverdue ? '⚠️' : isToday ? '🔔' : '📅'}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:'0.85rem', marginBottom:2 }}>{f.entity_name}</div>
                    {f.notes && (
                      <div style={{ fontSize:'0.75rem', color:'var(--text-secondary)', fontStyle:'italic' }}>"{f.notes}"</div>
                    )}
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontSize:'0.78rem', fontWeight:700, color: isOverdue ? '#DC2626' : isToday ? '#D97706' : '#2563EB' }}>
                      {isOverdue ? 'Overdue' : isToday ? 'Today' : new Date(f.follow_up_date).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}
                    </div>
                    <div style={{ fontSize:'0.68rem', color:'var(--text-muted)', marginTop:1, textTransform:'capitalize' }}>
                      {f.activity_type}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function TodayPlan({ plan, loading }) {
  const ACT_COLORS = {
    'Field beat': '#2563EB', 'KOL / institutional day': '#7C3AED',
    'Follow-up / consumable day': '#16A34A', 'Monthly review / pipeline': '#D97706',
    'Weekly Off': '#9CA3AF', 'Distributor review': '#DC2626',
  };
  return (
    <div className="card">
      <div className="card-header">
        <h3 style={{ fontSize:'0.95rem' }}>Today's beat plan</h3>
        <span style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{plan.length} activities</span>
      </div>
      <div className="card-body" style={{ paddingTop:10 }}>
        {loading ? <Spinner /> : plan.length === 0
          ? <Empty text="No activities planned today" />
          : plan.map((row, i) => (
            <div key={i} style={{ display:'flex', gap:10, padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
              <div style={{ width:3, borderRadius:2, background: ACT_COLORS[row.activity_type] || '#403EC9', flexShrink:0, alignSelf:'stretch' }} />
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:'0.82rem', fontWeight:600 }}>{row.activity_type}</div>
                <div style={{ fontSize:'0.72rem', color:'var(--text-secondary)' }}>
                  {row.city_cluster} · {row.planned_calls} calls · {row.planned_demos} demos
                </div>
                {row.focus_accounts && row.focus_accounts !== 'nan' && (
                  <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {row.focus_accounts}
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function FollowUpsPanel({ items, loading }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3 style={{ fontSize:'0.95rem' }}>Follow-ups due</h3>
        {items.length > 0 && <span className="badge badge-danger">{items.length}</span>}
      </div>
      <div className="card-body" style={{ paddingTop:10 }}>
        {loading ? <Spinner /> : items.length === 0
          ? <Empty text="✓ No overdue follow-ups" />
          : items.slice(0, 6).map(v => (
            <div key={v.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'1px solid var(--border)' }}>
              <div>
                <div style={{ fontSize:'0.82rem', fontWeight:600 }}>{v.doctor_name}</div>
                <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{v.district}</div>
              </div>
              <span className="badge badge-danger" style={{ fontSize:'0.65rem' }}>
                Due {v.follow_up_date?.split('T')[0]}
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}

function PendingPanel({ items, loading }) {
  const [list, setList] = useState(items);
  useEffect(() => setList(items), [items]);

  const act = async (id, action) => {
    try {
      await api.patch(`/actuals/${id}/approve`, { action });
      setList(l => l.filter(x => x.id !== id));
    } catch {}
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 style={{ fontSize:'0.95rem' }}>Pending approvals</h3>
        {list.length > 0 && <span className="badge badge-warning">{list.length}</span>}
      </div>
      <div className="card-body" style={{ paddingTop:10 }}>
        {loading ? <Spinner /> : list.length === 0
          ? <Empty text="✓ All clear" />
          : list.slice(0, 5).map(a => (
            <div key={a.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'1px solid var(--border)', fontSize:'0.82rem' }}>
              <div>
                <div style={{ fontWeight:600 }}>{a.product_name}</div>
                <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{a.doctor_name || 'No doctor'} · {a.logged_by_name}</div>
              </div>
              <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                <span style={{ fontWeight:700, marginRight:4 }}>₹{Number(a.revenue||0).toLocaleString()}</span>
                <button style={{ background:'var(--success-bg)', color:'var(--success)', border:'none', padding:'3px 8px', borderRadius:4, cursor:'pointer', fontSize:'0.75rem' }} onClick={() => act(a.id, 'approved')}>✓</button>
                <button style={{ background:'var(--danger-bg)', color:'var(--danger)', border:'none', padding:'3px 8px', borderRadius:4, cursor:'pointer', fontSize:'0.75rem' }} onClick={() => act(a.id, 'rejected')}>✗</button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

// ─── UX FIX 4 + 5: VP dashboard with resource alerts + targets ────
function VPDashboard() {
  const { user } = useAuth();
  const [sum, setSum]             = useState(null);
  const [territories, setTerr]    = useState([]);
  const [trend, setTrend]         = useState([]);

  useEffect(() => {
    api.get('/dashboard/summary').then(r => setSum(r.data.data)).catch(() => {});
    api.get('/dashboard/territory-snapshot').then(r => setTerr(r.data.data)).catch(() => {});
    api.get('/dashboard/monthly-trend').then(r => setTrend(r.data.data)).catch(() => {});
  }, []);

  const rev = sum?.revenue || {};
  const alerts = sum?.resource_alerts || [];
  const role = user?.role_slug || user?.role;
  const title = role === 'vp_sales' ? 'VP Sales Dashboard' : 'Sales Dashboard';

  return (
    <AppLayout title={title} subtitle="All territories — FY 2026-27">
      <TasksWidget />
      {alerts.length > 0 && (
        <div style={{ background:'#FEF3C7', border:'1px solid #D97706', borderRadius:'var(--radius-md)', padding:'10px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:16 }}>⚠️</span>
          <div>
            <span style={{ fontWeight:600, color:'#92400E', fontSize:'0.875rem' }}>Resource plan alert — </span>
            <span style={{ fontSize:'0.82rem', color:'#92400E' }}>
              {alerts.map(a => `${a.territory} (${a.ratio}x coverage)`).join(', ')} {alerts.length === 1 ? 'is' : 'are'} below 1.0 call coverage ratio.
              {' '}<a href="/territories" style={{ color:'#92400E', fontWeight:600 }}>View resource plan →</a>
            </span>
          </div>
        </div>
      )}

      <div className="kpi-grid">
        <KpiCard label="YTD Revenue"       value={fmtFull(rev.ytd_actual)}        color="#403EC9" icon="₹"
          sub={`of ${fmt(rev.annual_target)} target`}
          change={rev.achievement_pct >= 50 ? 'on track' : 'behind'} changeType={rev.achievement_pct >= 50 ? 'up' : 'down'} />
        <KpiCard label="MTD Revenue"       value={fmtFull(rev.mtd_actual)}        color="#16A34A" icon="📈"
          sub={`of ${fmt(rev.monthly_target)} monthly target`} />
        <KpiCard label="Annual Target"     value={fmt(rev.annual_target)}     color="#2563EB" icon="🎯" sub="FY 2026-27" />
        <KpiCard label="Pending Approvals" value={sum?.pending_approvals || 0} color={sum?.pending_approvals > 0 ? '#DC2626' : '#16A34A'} icon="⏳" href="/sales-orders?status=pending_approval" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:16 }}>
        <div className="card">
          <div className="card-header"><h3 style={{ fontSize:'0.95rem' }}>Revenue trend · FY 2026-27</h3></div>
          <div className="card-body" style={{ paddingTop:10 }}>
            <RevenueChart trend={trend} />
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3 style={{ fontSize:'0.95rem' }}>Territory achievement</h3></div>
          <div className="card-body" style={{ paddingTop:10 }}>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={territories} layout="vertical">
                <XAxis type="number" tickFormatter={v => `${v}%`} tick={{ fontSize:9 }} domain={[0, 100]} />
                <YAxis dataKey="territory" type="category" tick={{ fontSize:9 }} width={80} />
                <Tooltip formatter={v => `${v}%`} />
                <Bar dataKey="achievement_pct" radius={[0,3,3,0]} name="Achievement %">
                  {territories.map((t,i) => (
                    <Cell key={i} fill={t.achievement_pct >= 80 ? '#16A34A' : t.achievement_pct >= 50 ? '#403EC9' : '#D97706'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <TerritoryTable territories={territories} showTargets />
    </AppLayout>
  );
}

function ExecutiveDashboard() {
  const { user } = useAuth();
  const [sum, setSum]           = useState(null);
  const [territories, setTerr]  = useState([]);
  const [productMix, setMix]    = useState([]);
  const [trend, setTrend]       = useState([]);

  useEffect(() => {
    api.get('/dashboard/summary').then(r => setSum(r.data.data)).catch(() => {});
    api.get('/dashboard/territory-snapshot').then(r => setTerr(r.data.data)).catch(() => {});
    api.get('/dashboard/product-mix').then(r => setMix(r.data.data)).catch(() => {});
    api.get('/dashboard/monthly-trend').then(r => setTrend(r.data.data)).catch(() => {});
  }, []);

  const rev = sum?.revenue || {};
  const doc = sum?.doctor_universe || {};
  const alerts = sum?.resource_alerts || [];


  return (
    <AppLayout title={`${user?.role_name || 'Executive'} Overview`} subtitle="Alveofit commercial performance — FY 2026-27">
      <TasksWidget />
      {alerts.length > 0 && (
        <div style={{ background:'#FEF3C7', border:'1px solid #D97706', borderRadius:'var(--radius-md)', padding:'10px 16px', marginBottom:16, display:'flex', gap:10, alignItems:'center' }}>
          <span style={{ fontSize:16 }}>⚠️</span>
          <span style={{ fontSize:'0.82rem', color:'#92400E' }}>
            <strong>Resource plan alert:</strong> {alerts.map(a => `${a.territory} (${a.ratio}x)`).join(', ')} below required coverage.
          </span>
        </div>
      )}

      <div className="kpi-grid">
        <KpiCard label="YTD Revenue"     value={fmtFull(rev.ytd_actual)}               color="#403EC9" icon="₹" sub={`${rev.achievement_pct || 0}% of ${fmt(rev.annual_target)}`} change={rev.achievement_pct >= 50 ? 'on track' : 'behind'} changeType={rev.achievement_pct >= 50 ? 'up' : 'down'} />
        <KpiCard label="Annual Target"   value={fmt(rev.annual_target)}            color="#2563EB" icon="🎯" sub="FY 2026-27" />
        <KpiCard label="Doctor Universe" value={Number(doc.total || 0).toLocaleString()} color="#7C3AED" icon="👨‍⚕️" sub={`${doc.priority_a || 0} priority A`} />
        <KpiCard label="MTD Revenue"     value={fmtFull(rev.mtd_actual)}               color="#16A34A" icon="📈" sub={`of ${fmt(rev.monthly_target)} target`} />
        <KpiCard label="Territories"     value={territories.length}                color="#0369A1" icon="🗺️" />
        <KpiCard label="FOT Installed"   value={doc.fot_count || 0}               color="#D97706" icon="⚙️" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:14, marginBottom:16 }}>
        <div className="card">
          <div className="card-header"><h3 style={{ fontSize:'0.95rem' }}>Revenue trend · FY 2026-27</h3></div>
          <div className="card-body" style={{ paddingTop:10 }}>
            <RevenueChart trend={trend} />
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3 style={{ fontSize:'0.95rem' }}>Product mix</h3></div>
          <div className="card-body" style={{ paddingTop:8 }}>
            {productMix.some(p => p.actual_revenue > 0) ? (
              <>
                <ResponsiveContainer width="100%" height={110}>
                  <PieChart>
                    <Pie data={productMix.filter(p => p.actual_revenue > 0)} dataKey="actual_revenue" cx="50%" cy="50%" outerRadius={50} innerRadius={28}>
                      {productMix.filter(p => p.actual_revenue > 0).map((_, i) => <Cell key={i} fill={CLR[i % CLR.length]} />)}
                    </Pie>
                    <Tooltip formatter={v => fmt(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ fontSize:'0.72rem' }}>
                  {productMix.filter(p => p.actual_revenue > 0).slice(0, 5).map((p, i) => (
                    <div key={p.name} style={{ display:'flex', gap:5, alignItems:'center', marginBottom:3 }}>
                      <div style={{ width:7, height:7, borderRadius:'50%', background:CLR[i % CLR.length], flexShrink:0 }} />
                      <span style={{ flex:1, color:'var(--text-secondary)' }}>{p.name}</span>
                      <span style={{ fontWeight:600 }}>{fmt(p.actual_revenue)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ height:140, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)', fontSize:'0.8rem', textAlign:'center', padding:'0 12px' }}>
                Target: {fmt(productMix.reduce((s,p) => s + Number(p.target_revenue||0), 0))}<br/>
                <span style={{ fontSize:'0.72rem', marginTop:4 }}>Actuals will appear when sales are logged</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <TerritoryTable territories={territories} showTargets />
    </AppLayout>
  );
}

function AdminDashboard() {
  const [sum, setSum]         = useState(null);
  const [territories, setTerr] = useState([]);
  const [trend, setTrend]     = useState([]);

  useEffect(() => {
    api.get('/dashboard/summary').then(r => setSum(r.data.data)).catch(() => {});
    api.get('/dashboard/territory-snapshot').then(r => setTerr(r.data.data)).catch(() => {});
    api.get('/dashboard/monthly-trend').then(r => setTrend(r.data.data)).catch(() => {});
  }, []);

  const rev = sum?.revenue || {};
  const doc = sum?.doctor_universe || {};
  const alerts = sum?.resource_alerts || [];


  return (
    <AppLayout title="Dashboard">
      {alerts.length > 0 && (
        <div style={{ background:'#FEF3C7', border:'1px solid #D97706', borderRadius:'var(--radius-md)', padding:'10px 16px', marginBottom:16, display:'flex', gap:10 }}>
          <span style={{ fontSize:16 }}>⚠️</span>
          <span style={{ fontSize:'0.82rem', color:'#92400E' }}>
            <strong>Resource alert:</strong> {alerts.map(a => `${a.territory} (${a.ratio}x)`).join(', ')} below 1.0 coverage.
            <a href="/territories" style={{ color:'#92400E', fontWeight:600, marginLeft:8 }}>Review →</a>
          </span>
        </div>
      )}

      <TasksWidget />
      <div className="kpi-grid">
        <KpiCard label="YTD Revenue"       value={fmtFull(rev.ytd_actual)}            color="#403EC9" icon="₹"   sub={`${rev.achievement_pct || 0}% of ${fmt(rev.annual_target)}`} />
        <KpiCard label="MTD Revenue"       value={fmtFull(rev.mtd_actual)}            color="#16A34A" icon="📈"  sub={`of ${fmt(rev.monthly_target)} target`} />
        <KpiCard label="Doctor Universe"   value={Number(doc.total||0).toLocaleString()} color="#7C3AED" icon="👨‍⚕️" sub={`${doc.priority_a||0} priority A`} />
        <KpiCard label="Pending Approvals" value={sum?.pending_approvals || 0}    color={sum?.pending_approvals > 0 ? '#DC2626' : '#16A34A'} icon="⏳" />
        <KpiCard label="FOT Installed"     value={doc.fot_count || 0}            color="#D97706" icon="⚙️" />
        <KpiCard label="New Doctors"       value={doc.new_count || 0}            color="#0369A1" icon="🆕" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:16 }}>
        <div className="card">
          <div className="card-header"><h3 style={{ fontSize:'0.95rem' }}>Revenue trend · FY 2026-27</h3></div>
          <div className="card-body" style={{ paddingTop:10 }}>
            <RevenueChart trend={trend} />
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3 style={{ fontSize:'0.95rem' }}>Territory achievement</h3></div>
          <div className="card-body" style={{ paddingTop:10 }}>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={territories} layout="vertical">
                <XAxis type="number" tickFormatter={v => `${v}%`} tick={{ fontSize:9 }} domain={[0,100]} />
                <YAxis dataKey="territory" type="category" tick={{ fontSize:9 }} width={80} />
                <Tooltip formatter={v => `${v}%`} />
                <Bar dataKey="achievement_pct" radius={[0,3,3,0]}>
                  {territories.map((t,i) => <Cell key={i} fill={t.achievement_pct>=80?'#16A34A':t.achievement_pct>=50?'#403EC9':'#D97706'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <TerritoryTable territories={territories} showTargets />
    </AppLayout>
  );
}


// ─── Finance Dashboard ────────────────────────────────────────────
function FinanceDashboard() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/finance/dashboard').catch(() => ({ data: { data: null } })),
      api.get('/finance/claims-queue', { params: { status: 'ceo_approved', limit: 8, page: 1 } }).catch(() => ({ data: { data: [] } })),
    ]).then(([d, q]) => {
      setDash(d.data.data);
      setPending(q.data.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const kpi = dash?.kpi || {};

  const greet = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  };

  const STATUS_COLOR = {
    draft: '#6B7280', submitted: '#D97706', rm_approved: '#2563EB',
    rsm_approved: '#7C3AED', ceo_approved: '#16A34A', held: '#DC2626',
    queried: '#F59E0B', paid: '#059669', rejected: '#DC2626',
  };

  return (
    <AppLayout title="Finance Overview" subtitle={`Expense approvals & disbursements · ${new Date().toLocaleDateString('en-IN', {weekday:'long', day:'numeric', month:'long'})}`}>
      <TasksWidget />
      {/* Hero */}
      <div style={{borderRadius:16,background:'linear-gradient(135deg,#0369A1 0%,#0EA5E9 100%)',color:'#fff',padding:'28px 32px',marginBottom:24,position:'relative',overflow:'hidden'}}>
        <div style={{position:'absolute',right:32,top:'50%',transform:'translateY(-50%)',fontSize:72,opacity:0.12}}>💼</div>
        <div style={{fontSize:'0.8rem',opacity:0.85,marginBottom:4}}>{greet()}</div>
        <div style={{fontSize:'1.5rem',fontWeight:700}}>{user?.name || user?.email}</div>
        <div style={{fontSize:'0.85rem',opacity:0.75,marginTop:4}}>Finance · Expense Approval Console</div>
      </div>

      {/* KPI tiles */}
      {loading ? <div style={{textAlign:'center',padding:40}}><div className="spinner"/></div> : (
        <>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:20}}>
            {[
              { label:'Pending at Finance', value: kpi.pending_cnt ?? '—', icon:'⏳', color:'#D97706' },
              { label:'Hard Violations', value: kpi.hard_violation_cnt ?? '—', icon:'🚨', color:'#DC2626' },
              { label:'Advances Outstanding', value: kpi.advances_cnt ?? '—', icon:'🔒', color:'#7C3AED' },
              { label:'Paid This Month', value: kpi.paid_mtd_cnt ?? '—', icon:'💰', color:'#0369A1' },
            ].map(k => (
              <div key={k.label} style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:10,padding:'14px 18px',borderTop:`3px solid ${k.color}`}}>
                <div style={{fontSize:'1.4rem',marginBottom:4}}>{k.icon}</div>
                <div style={{fontSize:'0.72rem',color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:'0.04em'}}>{k.label}</div>
                <div style={{fontSize:'1.6rem',fontWeight:700,color:k.color,marginTop:2}}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Amount KPIs */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:14,marginBottom:24}}>
            {[
              { label:'Amount Pending (₹)', value: kpi.pending_amt ? fmt(kpi.pending_amt) : '—', color:'#D97706' },
              { label:'Advances Outstanding (₹)', value: kpi.advances_amt ? fmt(kpi.advances_amt) : '—', color:'#7C3AED' },
              { label:'Amount Paid MTD (₹)', value: kpi.paid_mtd_amt ? fmt(kpi.paid_mtd_amt) : '—', color:'#0369A1' },
            ].map(k => (
              <div key={k.label} style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:10,padding:'14px 18px'}}>
                <div style={{fontSize:'0.72rem',color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:4}}>{k.label}</div>
                <div style={{fontSize:'1.4rem',fontWeight:700,color:k.color}}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Pending queue preview */}
          <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden',marginBottom:20}}>
            <div style={{padding:'12px 18px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10}}>
              <div style={{fontWeight:600,fontSize:'0.95rem',flex:1}}>CEO-Approved — Ready to Pay</div>
              <a href="/finance" style={{fontSize:'0.8rem',color:'var(--brand-purple)',textDecoration:'none',fontWeight:500}}>View all →</a>
            </div>
            {pending.length === 0 ? (
              <div style={{padding:32,textAlign:'center',color:'var(--text-muted)',fontSize:'0.85rem'}}>✓ No claims pending payment</div>
            ) : (
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr style={{background:'var(--bg-subtle)'}}>
                  {['Rep','Territory','Period','Gross','Status','Aging'].map(h => (
                    <th key={h} style={{padding:'8px 14px',textAlign:'left',fontSize:'0.72rem',color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:'0.04em'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {pending.map(c => {
                    const days = Math.floor((Date.now() - new Date(c.ceo_approved_at || c.submitted_at || c.created_at)) / 86400000);
                    return (
                      <tr key={c.id} style={{borderTop:'1px solid var(--border)'}}>
                        <td style={{padding:'10px 14px',fontSize:'0.85rem',fontWeight:500}}>{c.rep_name}</td>
                        <td style={{padding:'10px 14px',fontSize:'0.82rem',color:'var(--text-secondary)'}}>{c.territory_name || '—'}</td>
                        <td style={{padding:'10px 14px',fontSize:'0.82rem'}}>{c.period_year}-{String(c.period_month).padStart(2,'0')}</td>
                        <td style={{padding:'10px 14px',fontWeight:600}}>₹{parseFloat(c.gross_amount||0).toLocaleString('en-IN',{maximumFractionDigits:0})}</td>
                        <td style={{padding:'10px 14px'}}>
                          <span style={{fontSize:'0.7rem',fontWeight:600,padding:'2px 8px',borderRadius:4,background:`${STATUS_COLOR[c.status]}22`,color:STATUS_COLOR[c.status],textTransform:'uppercase'}}>{c.status}</span>
                        </td>
                        <td style={{padding:'10px 14px',fontSize:'0.82rem',color: days > 15 ? '#DC2626' : days > 7 ? '#D97706' : 'var(--text-secondary)'}}>
                          {days}d {days > 15 ? '⚠' : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Quick links */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
            {[
              { label:'Claims Queue', desc:'Review & process approvals', icon:'📋', href:'/finance?tab=queue' },
              { label:'MIS Report', desc:'Period & territory breakdown', icon:'📊', href:'/finance?tab=mis' },
              { label:'Expense Policies', desc:'DA/TA/mobile rules', icon:'📜', href:'/expense-policies' },
            ].map(l => (
              <a key={l.label} href={l.href} style={{display:'block',textDecoration:'none',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:10,padding:'16px 18px',transition:'box-shadow 0.15s'}}
                onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 14px rgba(0,0,0,0.08)'}
                onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                <div style={{fontSize:'1.5rem',marginBottom:6}}>{l.icon}</div>
                <div style={{fontWeight:600,fontSize:'0.9rem',color:'var(--text-primary)'}}>{l.label}</div>
                <div style={{fontSize:'0.78rem',color:'var(--text-secondary)',marginTop:2}}>{l.desc}</div>
              </a>
            ))}
          </div>
        </>
      )}
    </AppLayout>
  );
}

// ─── Visit Recommendations Panel ─────────────────────────────────
function RecommendationsPanel({ onLogVisit }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/recommendations')
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const recs = data?.recommendations || [];
  const gap  = data?.target_gap;

  const scoreColor = (score) => {
    if (score >= 80) return '#DC2626';
    if (score >= 55) return '#D97706';
    return '#16A34A';
  };

  const priorityBadgeStyle = (priority) => ({
    display: 'inline-block', fontSize: '0.68rem', fontWeight: 600,
    padding: '1px 7px', borderRadius: 99,
    background: priority === 'A' ? '#EEEDFE' : '#F1EFE8',
    color:      priority === 'A' ? '#403EC9' : '#5F5E5A',
  });

  if (loading || !recs.length) return null;

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-header">
        <div>
          <h3 style={{ fontSize: '0.95rem' }}>Visit these today</h3>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>
            Ranked by follow-up urgency, target gap &amp; journey stage
            {gap?.behind && (
              <span style={{ color: '#DC2626', marginLeft: 6, fontWeight: 600 }}>
                · Territory {gap.gap_pct}% behind target
              </span>
            )}
          </div>
        </div>
        <a href="/doctors" className="btn btn-ghost btn-sm">All doctors →</a>
      </div>
      <div className="card-body" style={{ paddingTop: 10 }}>
        {recs.map((doc, i) => (
          <div key={doc.id} style={{
            display: 'flex', gap: 12, padding: '12px 0',
            borderBottom: i < recs.length - 1 ? '1px solid var(--border)' : 'none',
            alignItems: 'flex-start',
          }}>
            <div style={{ width: 3, borderRadius: 2, background: scoreColor(doc.score), flexShrink: 0, alignSelf: 'stretch', minHeight: 48 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 3 }}>
                <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{doc.name}</span>
                <span style={priorityBadgeStyle(doc.priority)}>Priority {doc.priority}</span>
                {doc.overdue_follow_up && (
                  <span style={{ fontSize: '0.68rem', fontWeight: 600, padding: '1px 7px', borderRadius: 99, background: '#FEE2E2', color: '#DC2626' }}>Follow-up overdue</span>
                )}
                {doc.journey_stage && (
                  <span style={{ fontSize: '0.68rem', padding: '1px 7px', borderRadius: 99, background: '#F1EFE8', color: '#5F5E5A' }}>{doc.journey_stage.replace('_',' ')}</span>
                )}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: doc.talking_point ? 4 : 0 }}>
                {doc.district && <span>{doc.district} · </span>}{doc.reasons.join(' · ')}
              </div>
              {doc.talking_point && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Pitch: {doc.talking_point}</div>
              )}
              <div style={{ height: 3, background: 'var(--border)', borderRadius: 2, marginTop: 8, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: doc.score + '%', background: scoreColor(doc.score), borderRadius: 2 }} />
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: scoreColor(doc.score), lineHeight: 1 }}>{doc.score}</div>
                <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>score</div>
              </div>
              <button className="btn btn-primary btn-sm" style={{ fontSize: '0.72rem', padding: '3px 10px', whiteSpace: 'nowrap' }} onClick={() => onLogVisit(doc)}>Log Visit</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Shared components ────────────────────────────────────────────
function KpiCard({ label, value, sub, color, icon, change, changeType, href }) {
  const inner = (
    <>
      <div className="kpi-icon" style={{ '--icon-bg': color + '15', '--icon-color': color, fontSize:'1rem' }}>{icon}</div>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value ?? 0}</div>
      {(sub || change) && (
        <div className="kpi-sub">
          {sub && <span>{sub}</span>}
          {change && <span className={`kpi-change ${changeType}`}>{change}</span>}
        </div>
      )}
    </>
  );
  if (href) return (
    <Link to={href} className="kpi-card" style={{ '--accent-color': color, textDecoration:'none', cursor:'pointer' }}>
      {inner}
    </Link>
  );
  return <div className="kpi-card" style={{ '--accent-color': color }}>{inner}</div>;
}

function TerritoryTable({ territories, showTargets }) {
  return (
    <div className="card">
      <div className="card-header" style={{ padding:'14px 20px 10px' }}>
        <h3 style={{ fontSize:'0.95rem' }}>Territory snapshot</h3>
        <a href="/territories" className="btn btn-ghost btn-sm">Full view →</a>
      </div>
      <div className="card-body no-pad">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Territory</th><th>Rep</th><th>Doctors</th>
                {showTargets && <th>Monthly target</th>}
                <th>MTD</th><th>YTD</th><th>Achievement</th><th>Visits</th>
              </tr>
            </thead>
            <tbody>
              {territories.map(t => (
                <tr key={t.id}>
                  <td style={{ fontWeight:600 }}>{t.territory}</td>
                  <td style={{ fontSize:'0.82rem', color:'var(--text-secondary)' }}>
                    {t.rep_name || <span style={{ color:'var(--warning)', fontStyle:'italic' }}>Hire needed</span>}
                  </td>
                  <td>{Number(t.doctor_count || 0).toLocaleString()}</td>
                  {showTargets && <td style={{ color:'var(--text-secondary)', fontSize:'0.82rem' }}>{fmt(t.annual_target / 12)}</td>}
                  <td>{fmt(t.mtd_revenue)}</td>
                  <td style={{ fontWeight:600 }}>{fmt(t.ytd_revenue)}</td>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <div className="progress-bar" style={{ width:60 }}>
                        <div className={`progress-bar-fill ${t.achievement_pct >= 80 ? 'success' : t.achievement_pct >= 50 ? '' : 'warning'}`}
                          style={{ width:`${Math.min(t.achievement_pct || 0, 100)}%` }} />
                      </div>
                      <span style={{ fontSize:'0.78rem', fontWeight:600 }}>{t.achievement_pct || 0}%</span>
                    </div>
                  </td>
                  <td>{t.visits_mtd || 0}</td>
                </tr>
              ))}
              {!territories.length && (
                <tr><td colSpan="8" style={{ textAlign:'center', padding:32, color:'var(--text-muted)' }}>No territory data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Spinner() {
  return <div style={{ textAlign:'center', padding:24, color:'var(--text-muted)' }}><div className="spinner" style={{ margin:'0 auto' }} /></div>;
}
function Empty({ text }) {
  return <div style={{ textAlign:'center', padding:'24px 0', color:'var(--text-muted)', fontSize:'0.82rem' }}>{text}</div>;
}

// ─── UX FIX 2: 3-tap visit modal — essentials first ──────────────
function QuickVisitModal({ onClose, prefilledDoctor }) {
  const OUTCOMES = [
    { key:'demo_done',   label:'Demo done',         icon:'🎯', color:'#16A34A' },
    { key:'follow_up',   label:'Follow-up set',     icon:'📅', color:'#2563EB' },
    { key:'reorder',     label:'Reorder / consumable', icon:'🔄', color:'#D97706' },
    { key:'no_answer',   label:'Not available',     icon:'📵', color:'#9CA3AF' },
  ];

  const [step, setStep]         = useState(prefilledDoctor ? 2 : 1);
  const [doctor, setDoctor]     = useState(prefilledDoctor);
  const [doctorSearch, setDSearch] = useState(prefilledDoctor?.name || '');
  const [searchResults, setResults] = useState([]);
  const [outcome, setOutcome]   = useState(null);
  const [details, setDetails]   = useState({ products_discussed:[], follow_up_date:'', notes:'', demo_done:false });
  const [showDetails, setShowDetails] = useState(false);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    if (doctorSearch.length > 2 && !doctor) {
      api.get('/doctors', { params:{ search: doctorSearch, limit:6 } })
        .then(r => setResults(r.data.data)).catch(() => {});
    } else { setResults([]); }
  }, [doctorSearch, doctor]);

  const PRODUCTS = ['alveoair Clinic','alveoFlow FOT','alveoMD','alveoMDlite','alveoASHA','6MWT','Viral Filters','Turbine','Mouthpiece'];

  const save = async () => {
    if (!doctor || !outcome) return;
    setSaving(true);
    try {
      const form = {
        doctor_id: doctor.id,
        visit_date: new Date().toISOString().split('T')[0],
        visit_type: outcome === 'demo_done' ? 'First Visit + Demo'
                  : outcome === 'follow_up' ? 'Follow-up / Demo'
                  : outcome === 'reorder'   ? 'Follow-up / Reorder' : 'Review',
        activity_type: 'Field beat',
        demo_done: outcome === 'demo_done' || details.demo_done,
        products_discussed: details.products_discussed,
        follow_up_date: details.follow_up_date || null,
        outcome: details.notes || OUTCOMES.find(o => o.key === outcome)?.label || '',
      };
      await api.post('/visits', form);
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to log visit');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:480 }}>
        <div className="modal-header">
          <div>
            <h3>Log Visit</h3>
            <div style={{ display:'flex', gap:6, marginTop:4 }}>
              {[1,2,3].map(s => (
                <div key={s} style={{ width:20, height:3, borderRadius:2, background: step >= s ? 'var(--brand-purple)' : 'var(--border)' }} />
              ))}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">

          {/* Step 1: Select doctor */}
          {step === 1 && (
            <div>
              <div style={{ fontSize:'0.82rem', color:'var(--text-secondary)', marginBottom:12 }}>Step 1 of 3 — Who did you visit?</div>
              <div style={{ position:'relative' }}>
                <input className="form-control" value={doctorSearch}
                  onChange={e => { setDSearch(e.target.value); setDoctor(null); }}
                  placeholder="Type doctor name…" autoFocus />
                {searchResults.length > 0 && !doctor && (
                  <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:100, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', boxShadow:'var(--shadow-lg)', maxHeight:200, overflowY:'auto' }}>
                    {searchResults.map(d => (
                      <div key={d.id} style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid var(--border)' }}
                        onClick={() => { setDoctor(d); setDSearch(d.name); setResults([]); }}>
                        <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{d.name}</div>
                        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{d.district} · {d.priority ? `Priority ${d.priority}` : ''}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {doctor && (
                <div style={{ marginTop:10, padding:'10px 12px', background:'var(--brand-purple-faint)', border:'1px solid var(--brand-purple)', borderRadius:'var(--radius-md)', display:'flex', gap:8, alignItems:'center' }}>
                  <span style={{ fontSize:16 }}>✓</span>
                  <div>
                    <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{doctor.name}</div>
                    <div style={{ fontSize:'0.72rem', color:'var(--text-secondary)' }}>{doctor.district} · Priority {doctor.priority}</div>
                  </div>
                </div>
              )}
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}>
                <button className="btn btn-primary" onClick={() => setStep(2)} disabled={!doctor}>Next →</button>
              </div>
            </div>
          )}

          {/* Step 2: Outcome — 4 big tap targets */}
          {step === 2 && (
            <div>
              <div style={{ fontSize:'0.82rem', color:'var(--text-secondary)', marginBottom:12 }}>Step 2 of 3 — What happened?</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:16 }}>
                {OUTCOMES.map(o => (
                  <button key={o.key} onClick={() => setOutcome(o.key)}
                    style={{ padding:'16px 10px', borderRadius:'var(--radius-md)', cursor:'pointer', textAlign:'center',
                      border: `2px solid ${outcome === o.key ? o.color : 'var(--border)'}`,
                      background: outcome === o.key ? o.color + '15' : 'var(--bg-card)',
                      transition:'all 0.15s' }}>
                    <div style={{ fontSize:24, marginBottom:5 }}>{o.icon}</div>
                    <div style={{ fontSize:'0.82rem', fontWeight:600, color: outcome === o.key ? o.color : 'var(--text-primary)' }}>{o.label}</div>
                  </button>
                ))}
              </div>

              {/* Optional details — collapsed by default */}
              <button className="btn btn-ghost btn-sm" onClick={() => setShowDetails(!showDetails)} style={{ marginBottom:showDetails ? 10 : 0 }}>
                {showDetails ? '▲ Hide' : '▼ Add'} products / notes / follow-up date
              </button>

              {showDetails && (
                <div style={{ background:'var(--bg)', borderRadius:'var(--radius-md)', padding:'12px 14px' }}>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize:'0.78rem' }}>Products discussed</label>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                      {PRODUCTS.map(p => (
                        <label key={p} style={{ display:'flex', alignItems:'center', gap:4, fontSize:'0.75rem', cursor:'pointer',
                          background: details.products_discussed.includes(p) ? 'var(--brand-purple-faint)' : 'var(--bg-card)',
                          border:`1px solid ${details.products_discussed.includes(p) ? 'var(--brand-purple)' : 'var(--border)'}`,
                          borderRadius:'var(--radius-sm)', padding:'3px 7px' }}>
                          <input type="checkbox" style={{ width:11, height:11 }} checked={details.products_discussed.includes(p)}
                            onChange={e => setDetails(d => ({ ...d, products_discussed: e.target.checked ? [...d.products_discussed, p] : d.products_discussed.filter(x => x !== p) }))} />
                          {p}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 12px' }}>
                    <div className="form-group">
                      <label className="form-label" style={{ fontSize:'0.78rem' }}>Follow-up date</label>
                      <input type="date" className="form-control" value={details.follow_up_date}
                        onChange={e => setDetails(d => ({ ...d, follow_up_date: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label" style={{ fontSize:'0.78rem' }}>Notes</label>
                      <input className="form-control" value={details.notes}
                        onChange={e => setDetails(d => ({ ...d, notes: e.target.value }))} placeholder="Quick note…" />
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display:'flex', justifyContent:'space-between', marginTop:14 }}>
                <button className="btn btn-ghost" onClick={() => setStep(1)}>← Back</button>
                <button className="btn btn-primary" onClick={() => setStep(3)} disabled={!outcome}>Next →</button>
              </div>
            </div>
          )}

          {/* Step 3: Confirm + save */}
          {step === 3 && (
            <div>
              <div style={{ fontSize:'0.82rem', color:'var(--text-secondary)', marginBottom:12 }}>Step 3 of 3 — Confirm</div>
              <div style={{ background:'var(--bg)', borderRadius:'var(--radius-md)', padding:'14px 16px', marginBottom:16 }}>
                <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:14 }}>👨‍⚕️</span>
                  <span style={{ fontWeight:600 }}>{doctor?.name}</span>
                </div>
                <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:14 }}>{OUTCOMES.find(o => o.key === outcome)?.icon}</span>
                  <span>{OUTCOMES.find(o => o.key === outcome)?.label}</span>
                </div>
                {details.products_discussed.length > 0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:6 }}>
                    {details.products_discussed.map(p => <span key={p} className="badge badge-purple" style={{ fontSize:'0.68rem' }}>{p}</span>)}
                  </div>
                )}
                {details.follow_up_date && (
                  <div style={{ fontSize:'0.78rem', color:'var(--text-secondary)', marginTop:6 }}>📅 Follow-up: {details.follow_up_date}</div>
                )}
                <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:6 }}>
                  📍 {new Date().toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' })}
                </div>
              </div>

              <div style={{ display:'flex', gap:10, justifyContent:'space-between' }}>
                <button className="btn btn-ghost" onClick={() => setStep(2)}>← Back</button>
                <button className="btn btn-primary" onClick={save} disabled={saving} style={{ minWidth:120 }}>
                  {saving ? 'Saving…' : '✓ Log Visit'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuickSaleModal({ onClose }) {
  const [products, setProducts] = useState([]);
  const [form, setForm]         = useState({ product_id:'', doctor_id:'', order_date:new Date().toISOString().split('T')[0], units:1, notes:'' });
  const [q, setQ]               = useState('');
  const [docs, setDocs]         = useState([]);
  const [saving, setSaving]     = useState(false);
  const set = (k,v) => setForm(f => ({ ...f, [k]:v }));

  useEffect(() => { api.get('/products').then(r => setProducts(r.data.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (q.length > 2) api.get('/doctors', { params:{ search:q, limit:6 }}).then(r => setDocs(r.data.data)).catch(() => {});
    else setDocs([]);
  }, [q]);

  const selProd = products.find(p => p.id === form.product_id);
  const estRev  = selProd ? form.units * selProd.asp_price : 0;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post('/actuals', form); onClose(); }
    catch (err) { alert(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><h3>Log Sale</h3><button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <form onSubmit={save}>
            <div className="form-group">
              <label className="form-label">Product *</label>
              <select className="form-control" value={form.product_id} onChange={e => set('product_id', e.target.value)} required>
                <option value="">Select product…</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name} — ₹{Number(p.asp_price).toLocaleString()}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ position:'relative' }}>
              <label className="form-label">Doctor (search)</label>
              <input className="form-control" value={q} onChange={e => { setQ(e.target.value); if(!e.target.value) set('doctor_id',''); }} placeholder="Type doctor name…" />
              {docs.length > 0 && !form.doctor_id && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:100, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', boxShadow:'var(--shadow-lg)', maxHeight:160, overflowY:'auto' }}>
                  {docs.map(d => <div key={d.id} style={{ padding:'9px 13px', cursor:'pointer', fontSize:'0.875rem', borderBottom:'1px solid var(--border)' }} onClick={() => { set('doctor_id', d.id); setQ(d.name); setDocs([]); }}><strong>{d.name}</strong> <span style={{ color:'var(--text-muted)' }}>— {d.district}</span></div>)}
                </div>
              )}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 14px' }}>
              <div className="form-group"><label className="form-label">Order Date *</label><input type="date" className="form-control" value={form.order_date} onChange={e => set('order_date', e.target.value)} required /></div>
              <div className="form-group"><label className="form-label">Units *</label><input type="number" className="form-control" value={form.units} onChange={e => set('units', parseInt(e.target.value)||1)} min="1" required /></div>
            </div>
            {estRev > 0 && (
              <div style={{ background:'var(--brand-purple-faint)', border:'1px solid var(--brand-purple)', borderRadius:'var(--radius-md)', padding:'10px 14px', marginBottom:12, fontSize:'0.875rem' }}>
                Estimated revenue: <strong>₹{estRev.toLocaleString()}</strong> — will be pending RSM approval
              </div>
            )}
            <div className="form-group"><label className="form-label">Notes</label><input className="form-control" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional…" /></div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Logging…' : 'Log Sale'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ─── Conversion Tasks Panel (RSM / VP / CEO / Director) ─── */
const CONV_OUTCOME_CONFIG = {
  won:            { label:'Won 🎉',              color:'#16A34A', bg:'#DCFCE7', next:'Mark as converted' },
  needs_followup: { label:'Still Following Up',  color:'#D97706', bg:'#FEF3C7', next:'Keep task open' },
  push_to_bde:    { label:'Return to BDE',       color:'#2563EB', bg:'#EFF6FF', next:'BDE will nurture more' },
  reassign:       { label:'Reassign to Rep',     color:'#7C3AED', bg:'#EDE9FE', next:'Pick another rep' },
};

function ConversionTasksPanel() {
  const [tasks, setTasks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [logTarget, setLogTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/bde/conversion-tasks').then(r => setTasks(r.data.data || [])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = tasks.filter(t => !['done','pushed_back','reassigned'].includes(t.status));

  if (!loading && tasks.length === 0) return null;

  return (
    <div className="card" style={{ marginTop:24 }}>
      <div className="card-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <h3 style={{ margin:0, fontSize:'1rem' }}>📋 Conversion Tasks</h3>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {open.length > 0 && <span style={{ fontSize:'0.75rem', background:'#FEF3C7', color:'#D97706', padding:'2px 8px', borderRadius:99, fontWeight:600 }}>{open.length} open</span>}
          <button className="btn btn-ghost btn-sm" onClick={load}>⟳</button>
        </div>
      </div>
      <div className="card-body" style={{ padding:0 }}>
        {loading ? <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)' }}>Loading…</div> : (
          <>
            {open.length === 0 && <div style={{ padding:'16px 20px', color:'var(--text-muted)', fontSize:'0.85rem' }}>No open conversion tasks.</div>}
            {open.map(t => (
              <div key={t.id} style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'flex-start', gap:14 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:'0.88rem' }}>{t.prospect_name || '—'}</div>
                  <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:2 }}>{t.action_required}</div>
                  {t.expected_outcome && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:2 }}>Target: {t.expected_outcome}</div>}
                  {t.due_date && <div style={{ fontSize:'0.75rem', color:'#D97706', marginTop:2 }}>Due: {new Date(t.due_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</div>}
                  <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:2 }}>Assigned by: {t.assigned_by_name||'—'}</div>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => setLogTarget(t)}>Log Outcome</button>
              </div>
            ))}
            {tasks.length >= 20 && (
              <div style={{ padding:'8px 16px', fontSize:'0.75rem', color:'var(--text-muted)', borderTop:'1px solid var(--border)', textAlign:'center' }}>
                Showing latest 20 open tasks
              </div>
            )}
          </>
        )}
      </div>
      {logTarget && <LogConversionOutcomeModal task={logTarget} onClose={() => setLogTarget(null)} onSaved={() => { setLogTarget(null); load(); }} />}
    </div>
  );
}

function LogConversionOutcomeModal({ task, onClose, onSaved }) {
  const [outcome, setOutcome]       = useState('');
  const [notes, setNotes]           = useState('');
  const [repSearch, setRepSearch]   = useState('');
  const [repResults, setRepResults] = useState([]);
  const [reassignTo, setReassignTo] = useState(null);
  const [soSearch, setSoSearch]     = useState('');
  const [soResults, setSoResults]   = useState([]);
  const [linkedSO, setLinkedSO]     = useState(null);
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    if (outcome !== 'reassign' || repSearch.length < 2) { setRepResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/users/search?q=${encodeURIComponent(repSearch)}&roles=rsm,vp_sales,ceo,director`).then(r => setRepResults(r.data.data || [])).catch(()=>{});
    }, 300);
    return () => clearTimeout(t);
  }, [repSearch, outcome]);

  useEffect(() => {
    if (outcome !== 'won' || soSearch.length < 2) { setSoResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/sales-orders?search=${encodeURIComponent(soSearch)}&limit=8`).then(r => setSoResults(r.data.data || r.data.orders || [])).catch(()=>{});
    }, 300);
    return () => clearTimeout(t);
  }, [soSearch, outcome]);

  const save = async () => {
    if (!outcome) return toast.error('Select an outcome');
    if (outcome === 'won' && !linkedSO) return toast.error('Link a Sales Order to mark as Won');
    if (outcome === 'reassign' && !reassignTo) return toast.error('Pick a rep to reassign to');
    setSaving(true);
    try {
      await api.patch(`/bde/conversion-tasks/${task.id}/outcome`, {
        outcome, notes,
        reassign_to: reassignTo?.id || null,
        sales_order_id: linkedSO?.id || null,
      });
      toast.success(outcome === 'won' ? `🎉 Deal won! SO ${linkedSO.order_number} linked.` : 'Outcome logged!');
      onSaved();
    } catch(err) {
      toast.error(err.response?.data?.message || 'Failed to log outcome');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:480 }}>
        <div className="modal-header">
          <h3>Log Conversion Outcome</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ background:'var(--bg-hover)', borderRadius:'var(--radius-md)', padding:'10px 14px', marginBottom:16, fontSize:'0.82rem' }}>
            <strong>{task.prospect_name || 'Prospect'}</strong>
            {task.action_required && <div style={{ color:'var(--text-muted)', marginTop:4 }}>{task.action_required}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">What happened? *</label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              {Object.entries(CONV_OUTCOME_CONFIG).map(([key, cfg]) => (
                <button key={key} type="button" onClick={() => { setOutcome(key); setReassignTo(null); }}
                  style={{ padding:'10px 12px', borderRadius:'var(--radius-md)', border:`2px solid ${outcome===key ? cfg.color : 'var(--border)'}`,
                    background: outcome===key ? cfg.bg : 'var(--bg-card)', color: outcome===key ? cfg.color : 'var(--text-primary)',
                    cursor:'pointer', textAlign:'left', fontSize:'0.82rem' }}>
                  <div style={{ fontWeight:600 }}>{cfg.label}</div>
                  <div style={{ fontSize:'0.7rem', opacity:0.7, marginTop:2 }}>→ {cfg.next}</div>
                </button>
              ))}
            </div>
          </div>

          {outcome === 'won' && (
            <div className="form-group">
              <label className="form-label">Link Sales Order * <span style={{ fontWeight:400, color:'var(--text-muted)' }}>(required to mark as Won)</span></label>
              {linkedSO ? (
                <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#DCFCE7', borderRadius:'var(--radius-md)', border:'1px solid #16A34A40' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, color:'#16A34A', fontSize:'0.88rem' }}>{linkedSO.order_number}</div>
                    <div style={{ fontSize:'0.75rem', color:'#166534' }}>
                      {linkedSO.customer_name || linkedSO.direct_customer_name}
                      {linkedSO.total_order_value ? ` · ₹${Number(linkedSO.total_order_value).toLocaleString('en-IN')}` : ''}
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setLinkedSO(null); setSoSearch(''); }}>✕</button>
                </div>
              ) : (
                <>
                  <input className="form-control" placeholder="Search by SO number or doctor name…"
                    value={soSearch} onChange={e => setSoSearch(e.target.value)} />
                  {soResults.length > 0 && (
                    <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-md)', marginTop:4, maxHeight:180, overflowY:'auto' }}>
                      {soResults.map(so => (
                        <div key={so.id} onClick={() => { setLinkedSO(so); setSoSearch(''); setSoResults([]); }}
                          style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid var(--border)', fontSize:'0.83rem' }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background=''}>
                          <div style={{ fontWeight:600 }}>{so.order_number}</div>
                          <div style={{ fontSize:'0.74rem', color:'var(--text-muted)' }}>
                            {so.customer_name || so.direct_customer_name}
                            {so.total_order_value ? ` · ₹${Number(so.total_order_value).toLocaleString('en-IN')}` : ''}
                            <span style={{ marginLeft:8, textTransform:'capitalize' }}>{so.order_status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize:'0.74rem', color:'var(--text-muted)', marginTop:6 }}>
                    No SO yet? <a href="/sales-orders/new" target="_blank" rel="noreferrer" style={{ color:'var(--primary)' }}>Create one →</a>
                  </div>
                </>
              )}
            </div>
          )}

          {outcome === 'reassign' && (
            <div className="form-group">
              <label className="form-label">Reassign to</label>
              <input className="form-control" placeholder="Search rep by name…" value={repSearch} onChange={e => setRepSearch(e.target.value)} />
              {repResults.length > 0 && (
                <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-md)', marginTop:4 }}>
                  {repResults.map(u => (
                    <div key={u.id} onClick={() => { setReassignTo(u); setRepSearch(u.name); setRepResults([]); }}
                      style={{ padding:'8px 12px', cursor:'pointer', fontSize:'0.84rem' }}
                      onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background=''}>
                      {u.name} <span style={{ color:'var(--text-muted)', fontSize:'0.75rem' }}>· {u.role}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any context for the team…" />
          </div>

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving || !outcome}>
              {saving ? 'Saving…' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
